import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { setupSuperAdmin, removeSuperAdmin, getSuperAdmins } from '../utils/setupSuperAdmin';

function SetupSuperAdmin() {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [superAdmins, setSuperAdmins] = useState([]);

  const handleSetupSuperAdmin = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage('Please enter an email address');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const result = await setupSuperAdmin(email.trim());
      setMessage(result.message);
      if (result.success) {
        setEmail('');
        loadSuperAdmins(); // Refresh the list
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSuperAdmin = async (emailToRemove) => {
    if (!window.confirm(`Are you sure you want to remove super admin status from ${emailToRemove}?`)) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const result = await removeSuperAdmin(emailToRemove);
      setMessage(result.message);
      if (result.success) {
        loadSuperAdmins(); // Refresh the list
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadSuperAdmins = async () => {
    try {
      const admins = await getSuperAdmins();
      setSuperAdmins(admins);
    } catch (error) {
      console.error('Error loading super admins:', error);
    }
  };

  // Load super admins on component mount
  React.useEffect(() => {
    loadSuperAdmins();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Setup Super Admin</h1>
          <p className="mt-2 text-gray-600">Configure super admin access for the Family Bible App</p>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Add Super Admin</h2>
          <form onSubmit={handleSetupSuperAdmin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                User Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="user@example.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Setting up...' : 'Make Super Admin'}
            </button>
          </form>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-md ${
            message.includes('Error') || message.includes('No user found') 
              ? 'bg-red-50 text-red-700 border border-red-200' 
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {message}
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Current Super Admins</h2>
          {superAdmins.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No super admins found</p>
          ) : (
            <div className="space-y-3">
              {superAdmins.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div>
                    <p className="font-medium text-gray-900">{admin.email}</p>
                    <p className="text-sm text-gray-500">
                      {admin.displayName || 'No name set'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveSuperAdmin(admin.email)}
                    disabled={loading || admin.id === currentUser?.uid}
                    className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                  >
                    {admin.id === currentUser?.uid ? 'Current User' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <a 
            href="/" 
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            ← Back to App
          </a>
        </div>
      </div>
    </div>
  );
}

export default SetupSuperAdmin;
